import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, Settings, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../AuthContext';
import { supabase } from '../supabaseClient';
import logoCompact from '../assets/images/logo_compact.png';
import logoCompactWhite from '../assets/images/logo_compact_white.png';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      if (!isAuthenticated) {
        setIsAdmin(false);
        return;
      }
      
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;
        
        if (!token) return;
        
        const response = await fetch(`${API_URL}/api/admin/setup-check`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setIsAdmin(data.is_admin);
        }
      } catch (err) {
        console.log('Admin check failed:', err);
      }
    };
    
    checkAdmin();
  }, [isAuthenticated]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Rooms', path: '/rooms' },
    { name: 'Facilities', path: '/facilities' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;
  const isHomePage = location.pathname === '/';

  // Determine text color based on scroll state (white text only on home page when not scrolled and menu closed)
  const textColor = (isHomePage && !isScrolled && !isMobileMenuOpen) ? '#ffffff' : 'var(--text-primary)';
  const underlineColor = (isHomePage && !isScrolled && !isMobileMenuOpen) ? '#ffffff' : 'var(--text-primary)';
  
  // Determine which logo to use based on scroll state and page
  const currentLogo = (isHomePage && !isScrolled && !isMobileMenuOpen) ? logoCompactWhite : logoCompact;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        (isScrolled || isMobileMenuOpen) ? 'bg-[var(--bg-primary)] shadow-sm' : 'bg-transparent'
      }`}
      style={{ borderBottom: (isScrolled || isMobileMenuOpen) ? '1px solid var(--border-light)' : 'none' }}
    >
      <div className="container mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="transition-opacity duration-300 hover:opacity-80"
          >
            <img 
              src={currentLogo} 
              alt="OlivOak Villa" 
              className="h-[120px] w-auto mt-7"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="relative py-2 text-sm font-medium transition-all duration-300"
                style={{
                  color: textColor,
                  textDecoration: 'none',
                  fontFamily: "'Inter', sans-serif"
                }}
              >
                {link.name}
                <span
                  className="absolute bottom-0 left-0 h-[1px] transition-all duration-500"
                  style={{
                    backgroundColor: underlineColor,
                    width: isActive(link.path) ? '100%' : '0%'
                  }}
                />
              </Link>
            ))}
          </nav>

          {/* Desktop CTA & User */}
          <div className="hidden lg:flex items-center gap-4">
            {isAuthenticated ? (
              <>
                {isAdmin && (
                  <Link to="/admin">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="transition-colors duration-300"
                      style={{ color: textColor }}
                      title="Admin Panel"
                      data-testid="admin-link"
                    >
                      <Settings className="h-5 w-5" />
                    </Button>
                  </Link>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex items-center gap-1 transition-colors duration-300"
                      style={{ color: textColor }}
                      data-testid="user-menu-trigger"
                    >
                      <User className="h-5 w-5" />
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="end" 
                    className="w-48"
                    style={{ 
                      backgroundColor: 'var(--bg-primary)', 
                      border: '1px solid var(--border-light)',
                      borderRadius: 0
                    }}
                  >
                    <DropdownMenuItem asChild>
                      <Link 
                        to="/profile" 
                        className="flex items-center gap-2 w-full cursor-pointer"
                        style={{ color: 'var(--text-primary)' }}
                        data-testid="profile-link"
                      >
                        <User className="h-4 w-4" />
                        My Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link 
                        to="/dashboard" 
                        className="flex items-center gap-2 w-full cursor-pointer"
                        style={{ color: 'var(--text-primary)' }}
                        data-testid="dashboard-link"
                      >
                        <Settings className="h-4 w-4" />
                        My Bookings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator style={{ backgroundColor: 'var(--border-light)' }} />
                    <DropdownMenuItem 
                      className="text-sm"
                      style={{ color: 'var(--text-secondary)' }}
                      disabled
                    >
                      {user?.email}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Link to="/booking">
                  <Button className="btn-primary">Book Now</Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="transition-colors duration-300" style={{ color: textColor }}>
                    Login
                  </Button>
                </Link>
                <Link to="/booking">
                  <Button className="btn-primary">Book Now</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden p-2 transition-colors duration-300"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={{ color: textColor }}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav
            className="lg:hidden py-6 border-t"
            aria-label="Mobile navigation"
            style={{ 
              borderColor: 'var(--border-light)',
              backgroundColor: 'var(--bg-primary)'
            }}
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-sm font-medium py-2"
                  style={{
                    color: isActive(link.path) ? 'var(--interactive-hover)' : 'var(--text-primary)',
                    textDecoration: 'none',
                    fontFamily: "'Inter', sans-serif"
                  }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <div className="flex flex-col gap-3 mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-light)' }}>
                {isAuthenticated ? (
                  <>
                    {isAdmin && (
                      <Link to="/admin" onClick={() => setIsMobileMenuOpen(false)}>
                        <Button variant="ghost" className="w-full" style={{ color: 'var(--text-primary)' }}>
                          Admin Panel
                        </Button>
                      </Link>
                    )}
                    <Link to="/profile" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full" style={{ color: 'var(--text-primary)' }}>
                        My Profile
                      </Button>
                    </Link>
                    <Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full" style={{ color: 'var(--text-primary)' }}>
                        My Bookings
                      </Button>
                    </Link>
                    <Link to="/booking" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button className="btn-primary w-full">Book Now</Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full" style={{ color: 'var(--text-primary)' }}>
                        Login
                      </Button>
                    </Link>
                    <Link to="/booking" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button className="btn-primary w-full">Book Now</Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};
